import FacebookPixel from "@components/FacebookPixel"
import { confidence } from "@helpers/confidence"
import initializeSherlockObserver from "@helpers/initializeSherlockObserver"
import ErrorPage from "@pages/ErrorPage"
import routes from "@pages/routes"
import * as Sentry from "@sentry/react"
import { newTracker } from "@snowplow/browser-tracker"
import { ConfidenceProvider } from "@spotify-confidence/react"
import { useEffect } from "react"
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from "react-router-dom"
import "./i18n"
import "./index.css"

if (import.meta.env.MODE !== "development") {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        createRoutesFromChildren,
        matchRoutes,
        useEffect: useEffect,
        useLocation,
        useNavigationType,
      }),
    ],
    tracesSampleRate: 0, // NOTE: Disable performance monitoring
  })
}

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter)

const router = sentryCreateBrowserRouter(routes)

// Created here rather than from an effect. React flushes effects child-first,
// so a route that renders in the same commit as `App` fires its page view
// before `App`'s effect has run — and Snowplow drops calls made while no
// tracker is registered, taking the event with them. Routes whose loaders
// defer the first render happen to avoid that, which makes it a property of
// the route rather than of the tracking. Nothing here needs React, so it runs
// at import time and the ordering question disappears.
newTracker("sp1", import.meta.env.VITE_SNOWPLOW_TRACKER_URL as string, {
  appId: import.meta.env.VITE_SNOWPLOW_APP_ID as string,
  platform: "web",
})

export default function App() {
  useEffect(() => {
    if (import.meta.env.MODE === "development") return

    const sherlockObserver = initializeSherlockObserver()

    sherlockObserver.observe({ entryTypes: ["resource"] })

    return () => {
      sherlockObserver.disconnect()
    }
  }, [])

  return (
    <Sentry.ErrorBoundary fallback={<ErrorPage />}>
      <FacebookPixel
        pixelId={import.meta.env.VITE_FACEBOOK_PIXEL_ID as string}
      />

      <ConfidenceProvider confidence={confidence}>
        <RouterProvider router={router} />
      </ConfidenceProvider>
    </Sentry.ErrorBoundary>
  )
}
